import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { HistoryEntry } from "@src/interfaces";
import { getHistory } from "@src/services";
import { usePrivySmartWallet } from "./PrivySmartWalletProvider";
import { isServer, logger } from "@src/utils";

export interface HistoryState {
  history: HistoryEntry[];
  historyLoadedOnce: boolean;
  addTransactionToLocalHistory: (
    transaction: HistoryEntry,
    walletAddress: string,
  ) => void;
  fetchHistory: () => Promise<void>;
}

const HistoryContext = createContext<HistoryState>({} as HistoryState);

interface HistoryProviderProps {
  chainIds?: string | string[];
  children: ReactNode;
}

const PENDING_TXS_LOCAL_STORAGE_KEY = "swapper_pending_transactions";

type LocalHistory = { [walletAddress: string]: HistoryEntry[] };

const getLocalHistory = (): LocalHistory => {
  if (!isServer) {
    try {
      return JSON.parse(
        localStorage.getItem(PENDING_TXS_LOCAL_STORAGE_KEY) || "{}",
      ) as LocalHistory;
    } catch (error) {
      logger.error("Error parsing local history:", error);
      return {};
    }
  }
  return {};
};

const setLocalHistory = (data: LocalHistory) => {
  if (!isServer) {
    localStorage.setItem(PENDING_TXS_LOCAL_STORAGE_KEY, JSON.stringify(data));
  }
};

export const HistoryProvider = ({
  chainIds,
  children,
}: HistoryProviderProps) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyLoadedOnce, setHistoryLoadedOnce] = useState(false);

  const { smartWalletAddress } = usePrivySmartWallet();

  const fetchHistory = useCallback(async () => {
    if (!smartWalletAddress) {
      setHistoryLoadedOnce(true);
      return;
    }
    try {
      const fetchedData = await getHistory({
        walletAddress: smartWalletAddress,
        chainIds:
          typeof chainIds === "string"
            ? [chainIds]
            : chainIds && chainIds?.length > 0
            ? chainIds
            : undefined,
      });

      // Create a map of txHash -> transaction from fetched data
      const txHashToFetchedTx: Record<string, HistoryEntry> = {};
      for (const transaction of fetchedData) {
        if (transaction.txHash) {
          txHashToFetchedTx[transaction.txHash] = transaction;
        }
      }

      // Get transactions from local storage
      const localHistory = getLocalHistory();
      const walletLocalHistory =
        localHistory[smartWalletAddress.toLowerCase()] || [];
      const newLocalHistory: LocalHistory = { ...localHistory };
      const newLocalHistoryForWallet: HistoryEntry[] = [];

      // Merge local transactions with fetched ones
      for (const transaction of walletLocalHistory) {
        if (transaction.txHash && !txHashToFetchedTx[transaction.txHash]) {
          // Local transaction not yet in backend - keep it
          fetchedData.push(transaction);
          newLocalHistoryForWallet.push(transaction);
        }
      }

      // Update local storage with transactions not yet in backend
      if (newLocalHistoryForWallet.length > 0) {
        newLocalHistory[smartWalletAddress.toLowerCase()] =
          newLocalHistoryForWallet;
        setLocalHistory(newLocalHistory);
      }

      // Sort transactions by date (newest first)
      setHistory(
        fetchedData.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        ),
      );
    } catch (error) {
      logger.error(error);
    } finally {
      setHistoryLoadedOnce(true);
    }
  }, [smartWalletAddress, chainIds]);

  const addTransactionToLocalHistory = useCallback(
    (transaction: HistoryEntry, walletAddress: string) => {
      setHistory((prevHistory) => {
        // Remove old tx if it exists with the same hash
        const clearedPrevHistory = transaction.txHash
          ? prevHistory.filter((tx) => tx.txHash !== transaction.txHash)
          : prevHistory;

        const localHistory = getLocalHistory();
        const updatedLocalHistory = {
          ...localHistory,
          [walletAddress.toLowerCase()]: [
            ...(localHistory[walletAddress.toLowerCase()] || []),
            transaction,
          ],
        };
        setLocalHistory(updatedLocalHistory);

        // Return updated history
        return [transaction, ...clearedPrevHistory].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
      });
    },
    [],
  );

  useEffect(() => {
    if (!smartWalletAddress) return;

    setHistoryLoadedOnce(false);
    setHistory([]);
    fetchHistory();
  }, [smartWalletAddress, fetchHistory]);

  const state = useMemo<HistoryState>(
    () => ({
      history,
      historyLoadedOnce,
      addTransactionToLocalHistory,
      fetchHistory,
    }),
    [history, historyLoadedOnce, addTransactionToLocalHistory, fetchHistory],
  );

  return (
    <HistoryContext.Provider value={state}>{children}</HistoryContext.Provider>
  );
};

export function useHistory(): HistoryState {
  return useContext(HistoryContext);
}
